// safeStorage — every Web Storage access wrapped in try/catch.
//
// Why this exists: in Safari private mode, with "block all cookies", or under an
// enterprise cookie policy, *touching the `localStorage` property itself* throws a
// `SecurityError` — not just `getItem()`. A bare `localStorage.getItem(...)` at module
// top level therefore aborts module evaluation, the SPA never renders, and the user
// sees a blank page before any error boundary exists.
//
// Contract:
//   get(key)         -> the stored string, or null on any failure (missing key included)
//   set(key, value)  -> true when written, false on any failure
//   remove(key)      -> true when removed, false on any failure
//   keys()           -> array of every stored key, [] on any failure
// Nothing here ever throws. The first failure per storage area is logged once (warn),
// later ones stay silent so a blocked store cannot spam the console on every state
// change. The flag lives in each wrapper, so a blocked sessionStorage still gets its
// own warning after localStorage has already warned (and vice versa).

// Re-arm callbacks of every wrapper created, for the test seam at the bottom.
const warningResets = new Set()

/**
 * Build a guarded wrapper around a Web Storage area.
 * @param {() => Storage|null|undefined} getStore Accessor; may itself throw.
 * @param {string} [label] Name used in the one-time warning.
 */
export function createSafeStorage(getStore, label = 'storage') {
  let hasWarned = false
  warningResets.add(() => {
    hasWarned = false
  })

  function warnOnce(op, key, err) {
    if (hasWarned) return
    hasWarned = true
    console.warn(
      `[safeStorage] ${label} unavailable (${op}${key ? ` "${key}"` : ''}) — ` +
        'continuing without persistence:',
      err
    )
  }

  return {
    /**
     * @param {string} key
     * @returns {string|null} stored value, or null when missing/unavailable
     */
    get(key) {
      try {
        const store = getStore()
        if (!store) return null
        const value = store.getItem(key)
        return value ?? null
      } catch (err) {
        warnOnce('get', key, err)
        return null
      }
    },

    /**
     * @param {string} key
     * @param {string} value
     * @returns {boolean} true when persisted
     */
    set(key, value) {
      try {
        const store = getStore()
        if (!store) return false
        store.setItem(key, value)
        return true
      } catch (err) {
        warnOnce('set', key, err)
        return false
      }
    },

    /**
     * @param {string} key
     * @returns {boolean} true when removed
     */
    remove(key) {
      try {
        const store = getStore()
        if (!store) return false
        store.removeItem(key)
        return true
      } catch (err) {
        warnOnce('remove', key, err)
        return false
      }
    },

    /**
     * @returns {string[]} every key currently stored, or [] when unavailable
     */
    keys() {
      try {
        const store = getStore()
        if (!store) return []
        const out = []
        for (let i = 0; i < store.length; i++) {
          const k = store.key(i)
          if (k != null) out.push(k)
        }
        return out
      } catch (err) {
        warnOnce('keys', null, err)
        return []
      }
    },
  }
}

// The accessors are lazy on purpose: the property lookup is what throws, so it must
// happen inside the try/catch of each call, never once at module scope.
export const safeLocal = createSafeStorage(
  () => (typeof window !== 'undefined' ? window.localStorage : globalThis.localStorage),
  'localStorage'
)

export const safeSession = createSafeStorage(
  () => (typeof window !== 'undefined' ? window.sessionStorage : globalThis.sessionStorage),
  'sessionStorage'
)

/** Test seam: re-arm the one-time warning of every wrapper. */
export function __resetSafeStorageWarning() {
  for (const reset of warningResets) reset()
}
